import i18next from 'i18next';
import { all, takeEvery, call, fork, take, put, select } from 'redux-saga/effects';
import { requestPuppy, requestNapi, isApiError } from '@wix/da-http-client';
import { uploadAttachment, uploadAttachmentImmediately, uploadAttachmentProgress, uploadAttachmentSuccess, uploadAttachmentFailure, cancelAttachmentUpload, removeAttachment, repositionAttachment, repositionAttachmentFail, repositionAttachmentSuccess, setAttachmentCaption, setMainDeviationCaption } from '../actions';
import { putDeviation, fetchDeviation, putErrorNotification } from '../../../utils/saga';
import { getExtendedDeviationById } from '../../deviations/selectors';
import { getAllAttachmentUploads } from '../selectors';
import { createUploadChannel } from './base';
import { uploadQueueWatcher } from './queue';
import { updateDeviationEntity } from '../../deviations/actionCreators';
function* createAttachmentUploadChannel(file, deviationId, attachmentType = 'pcp', position) {
    const url = 'deviation/attachments/add';
    const formData = new FormData();
    formData.append('attachment_file', file);
    formData.append('deviationid', `${deviationId}`);
    formData.append('type', attachmentType);
    formData.append('position', `${position}`);
    return yield call(createUploadChannel, url, formData, 'attachment');
}
function* uploadAttachmentHandler(action) {
    var _deviation_extended_additionalMedia, _deviation_extended;
    const { file, uploadId, parentDeviationId, type, onSuccess, onFailure } = action.payload;
    const deviation = yield select(getExtendedDeviationById, parentDeviationId);
    // attachments are added to the end of the attachment list, so we need to
    // calculate the attachment position by taking all the additionalMedia
    // (attachments) from the deviation, and all the active uploads, and set the
    // position to a value after that
    const allAttachmentUploads = yield select(getAllAttachmentUploads) || [];
    const attachmentUploadsForDeviation = allAttachmentUploads.filter((upload)=>upload.parentDeviationId === parentDeviationId);
    const position = ((deviation === null || deviation === void 0 ? void 0 : (_deviation_extended = deviation.extended) === null || _deviation_extended === void 0 ? void 0 : (_deviation_extended_additionalMedia = _deviation_extended.additionalMedia) === null || _deviation_extended_additionalMedia === void 0 ? void 0 : _deviation_extended_additionalMedia.length) || 0) + attachmentUploadsForDeviation.length;
    const channel = yield call(createAttachmentUploadChannel, file, parentDeviationId, type, position);
    // If a cancel action for this file id is fired we close the channel
    yield fork(function*() {
        yield take((a)=>a.type === cancelAttachmentUpload.toString() && a.payload.uploadId === uploadId);
        channel.close();
    });
    while(true){
        const { progress = 0, error, success, response } = yield take(channel);
        if (error) {
            yield put(uploadAttachmentFailure(uploadId, error));
            if (onFailure) {
                yield call(onFailure, {
                    uploadId
                }, error);
            }
            return;
        }
        if (success) {
            const { fileId, deviationId, attachment } = response;
            if (type === 'additional_image') {
                // manually update deviation in the store
                if (deviation === null || deviation === void 0 ? void 0 : deviation.extended) {
                    deviation.extended.additionalMedia = [
                        ...deviation.extended.additionalMedia || [],
                        attachment
                    ].sort((a, b)=>a.position > b.position ? 1 : -1);
                    yield call(putDeviation, deviation);
                }
            }
            yield put(uploadAttachmentSuccess(uploadId, fileId, deviationId, file));
            if (onSuccess) {
                yield call(onSuccess, {
                    uploadId,
                    fileId: fileId,
                    deviationId: deviationId,
                    file,
                    attachment
                });
            }
            return;
        }
        yield put(uploadAttachmentProgress(uploadId, progress));
    }
}
function* removeAttachmentHandler({ payload }) {
    var _deviation_extended;
    const { fileId, deviationId, type, onSuccess } = payload;
    const data = {
        fileid: fileId,
        deviationid: deviationId,
        type
    };
    const result = yield call(requestPuppy, {
        method: 'post',
        url: 'deviation/attachments/remove',
        data
    }, undefined);
    if (!result || isApiError(result) || !result.success) {
        yield putErrorNotification(i18next.t('somethingWentWrong'));
        return;
    }
    // Remove the attachment from the store
    const deviation = yield select(getExtendedDeviationById, deviationId);
    if (deviation === null || deviation === void 0 ? void 0 : (_deviation_extended = deviation.extended) === null || _deviation_extended === void 0 ? void 0 : _deviation_extended.additionalMedia) {
        deviation.extended.additionalMedia = deviation.extended.additionalMedia.filter((a)=>a.fileId !== fileId);
        yield call(putDeviation, deviation);
    }
    onSuccess();
}
function* repositionAttachmentHandler({ payload }) {
    var _deviation_extended;
    const { fileId, deviationId, type, position } = payload;
    // move attachment in the deviation immediately
    const deviation = yield select(getExtendedDeviationById, deviationId);
    if (!(deviation === null || deviation === void 0 ? void 0 : (_deviation_extended = deviation.extended) === null || _deviation_extended === void 0 ? void 0 : _deviation_extended.additionalMedia)) {
        yield put(repositionAttachmentFail(fileId, deviationId, type, position));
        return;
    }
    const attachments = deviation.extended.additionalMedia;
    if (!attachments) {
        yield put(repositionAttachmentFail(fileId, deviationId, type, position));
        return;
    }
    const attachment = attachments.find((a)=>a.fileId === fileId);
    if (!attachment) {
        yield put(repositionAttachmentFail(fileId, deviationId, type, position));
        return;
    }
    // Reposition on the server
    const data = {
        fileid: fileId,
        deviationid: deviationId,
        type: 'additional_image',
        position
    };
    const result = yield call(requestPuppy, {
        method: 'post',
        url: 'deviation/attachments/reposition',
        data
    }, undefined);
    if (!result || isApiError(result) || !result.success) {
        yield putErrorNotification(i18next.t('somethingWentWrong'));
        // reload deviation from the server
        yield call(fetchDeviation, deviationId);
    }
    // Safety check since at one point, this could return null
    if (result.deviation) {
        yield put(updateDeviationEntity(result.deviation));
    }
    yield put(repositionAttachmentSuccess(fileId, deviationId, type, position));
}
function* setAttachmentCaptionHandler({ payload }) {
    var _deviation_extended;
    const { fileId, deviationId, caption } = payload;
    // move attachment in the deviation immediately
    const deviation = yield select(getExtendedDeviationById, deviationId);
    if (!(deviation === null || deviation === void 0 ? void 0 : (_deviation_extended = deviation.extended) === null || _deviation_extended === void 0 ? void 0 : _deviation_extended.additionalMedia)) {
        return;
    }
    const attachments = deviation.extended.additionalMedia;
    if (!attachments) {
        return;
    }
    const attachment = attachments.find((a)=>a.fileId === fileId);
    if (!attachment) {
        return;
    }
    const data = {
        fileid: fileId,
        deviationid: deviationId,
        type: 'additional_image',
        editorRaw: JSON.stringify(caption)
    };
    // captions need node to transform editorRaw
    const result = yield call(requestNapi, {
        method: 'post',
        url: 'deviation/attachments/set_caption',
        data
    }, undefined);
    if (!result || isApiError(result) || !result.success) {
        yield putErrorNotification(i18next.t('somethingWentWrong'));
        return;
    }
    // update attachment in the store
    attachment.caption = caption;
    yield call(putDeviation, deviation);
}
function* setMainDeviationCaptionHandler({ payload }) {
    const { deviationId, caption } = payload;
    const deviation = yield select(getExtendedDeviationById, deviationId);
    if (!(deviation === null || deviation === void 0 ? void 0 : deviation.extended)) {
        yield putErrorNotification(i18next.t('somethingWentWrong'));
        return;
    }
    const data = {
        deviationid: deviationId,
        captionEditorRaw: JSON.stringify(caption)
    };
    // captions need node to transform editorRaw
    const result = yield call(requestNapi, {
        method: 'post',
        url: 'deviation/update',
        data
    }, undefined);
    if (!result || isApiError(result) || result.status !== 'success') {
        yield putErrorNotification(i18next.t('somethingWentWrong'));
        return;
    }
    // update deviation caption in the store
    deviation.extended.caption = caption;
    yield call(putDeviation, deviation);
}
export default function* saga() {
    yield all([
        takeEvery(uploadAttachmentImmediately, uploadAttachmentHandler),
        uploadQueueWatcher('attachment', [
            uploadAttachment,
            uploadAttachmentSuccess,
            uploadAttachmentFailure
        ], uploadAttachmentImmediately, true),
        takeEvery(removeAttachment, removeAttachmentHandler),
        takeEvery(repositionAttachment, repositionAttachmentHandler),
        takeEvery(setAttachmentCaption, setAttachmentCaptionHandler),
        takeEvery(setMainDeviationCaption, setMainDeviationCaptionHandler)
    ]);
}

//# sourceMappingURL=attachments.js.map